import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import { MdOutlineFileDownload } from "react-icons/md";
import {
  adminApprovaDeny,
  featureRestrictionApproval,
  getAdminApprovalData,
  getKPICardsRequestApprovaldetails,
} from "../../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../../utils/toast";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { formatFilterValue } from "../../../utils/formatFilters";
import KpiCard, { KpiCardprops } from "../../KpiCards/KpiCard";
import FeatureRestrictionModal, {
  ModalState,
} from "../../Modals/FeatureRestrictionModal";
import GridTableCommon from "../../GridTableCommon/GridTableCommon";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../utils/axiosInstance";
import { downloadBlobFile } from "../../../utils/commonUtil";

const AdminApproval: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [gridData, setGridData] = useState<GridTableDataType | any>({
    rows: [],
    totalCount: 0,
  });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [refresh, setrefresh] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [kpicardsData, setKpicardsData] = useState<Array<KpiCardprops>>([]);
  const defaultModalState: ModalState = {
    headLabel: "",
    activateOn: "noAction",
    isOpen: false,
    payload: [],
    isSyncSourceUserChangeProcess: false,
  };

  const [modalState, setModalState] = useState<ModalState>(defaultModalState);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const tableDataRef = useRef<{ [key: string]: string }>({});
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const buildSortingFilterPayload = () => ({
    filter:
      filters?.length > 0
        ? (filters || [])?.map((item: any) =>
            formatFilterValue(item?.value, item?.id),
          )
        : [],
    sort: sorting?.map((item: any) => {
      return { columnName: item?.id, type: item?.desc ? "DESC" : "ASC" };
    }),
    page: {
      limit: pagination?.rowsPerPage,
      offset: pagination?.page * pagination?.rowsPerPage,
    },
  });

  const handleKPICardClick = (data: KpiCardprops) => {
    setFilters([
      ...(filters || []).filter((d) => d?.id !== "new_status"),
      {
        id: "new_status",
        value: data?.label,
      },
    ]);
  };

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await getAdminApprovalData({
      sortingFilter: buildSortingFilterPayload(),
    });
    const kpiresp = await getKPICardsRequestApprovaldetails();
    setKpicardsData(
      kpiresp?.data?.map((d: any) => {
        return {
          ...d,
          label: d?.name,
          count: d?.status_count,
        };
      }) ?? [],
    );

    const gridResult = {
      ...resp?.data,
      columns: resp?.data?.columns?.map((column: any) => {
        if (column?.type === "MULTISELECT") {
          return { ...column, type: "MULTI_SELECT", minSize: 360 };
        }
        return column;
      }),
      rows: resp?.data?.rows?.map((item: any) => {
        return {
          ...item,
          disabled: item?.action_id?.length === 0,
          action_id:
            item?.action_id?.length > 0
              ? item?.action_id?.map((d: any) => {
                  if (d.value === "Approve") {
                    return {
                      ...d,
                      disabled:
                        tableDataRef?.current[item?.subrequest_id]?.length > 0
                          ? false
                          : item?.eligible_roles?.default?.every(
                              (role: any) => role?.isAutoSelect !== true,
                            ),
                    };
                  }
                  return d;
                })
              : [],
          eligible_roles: item?.approved_role_names
            ? {
                default: item?.approved_role_names
                  ?.split(",")
                  ?.map((role: string, index: number) => ({
                    id: `${index}`,
                    name: role.trim(),
                    label: role.trim(),
                    value: role.trim(),
                    disabled: true,
                  })),
                selected:
                  tableDataRef?.current[item?.subrequest_id] ||
                  item?.approved_role_names
                    ?.split(",")
                    ?.map((role: string, index: number) => ({
                      id: `${index}`,
                      name: role.trim(),
                      label: role.trim(),
                      value: role.trim(),
                      disabled: true,
                    })),
              }
            : "default" in (item?.eligible_roles ?? {})
              ? {
                  ...item?.eligible_roles,
                  selected:
                    tableDataRef?.current[item?.subrequest_id] ||
                    item?.eligible_roles?.default?.filter(
                      (d: any) => d?.isAutoSelect,
                    ),
                }
              : item?.eligible_roles,
          isSelectable: null,
        };
      }),
    };
    setGridData(gridResult);
    setIsLoading(false);
  };

  const handleDownloadRequests = async () => {
    try {
      const response = await axiosInstance.post(
        `/onboard/grid-export`,
        {
          serviceUrl: "/request/data/myapproval",
          sortingFilter: {
            ...buildSortingFilterPayload(),
            columns: [],
          },
          fileName: "admin_approval_requests",
          sheetName: "admin_approval",
        },
        {
          responseType: "blob",
          headers: {
            Accept: "application/vnd.ms-excel",
          },
        },
      );

      const disposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(
        /filename\*?=(?:UTF-8''|\")?([^\";]+)/i,
      );
      const fileName = decodeURIComponent(
        (fileNameMatch?.[1] || "admin_approval_requests.xlsx").replace(
          /\"/g,
          "",
        ),
      );

      downloadBlobFile(response?.data, fileName);
      triggerToast("Requests downloaded successfully", "success");
    } catch (error: any) {
      triggerToast(error?.message || "Unable to download requests", "error");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timeout);
  }, [sorting, pagination, refresh]);

  // Set filter when reqId is provided from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const reqId = searchParams.get("reqId");

    if (reqId) {
      setFilters([
        ...(filters || []).filter((d) => d?.id !== "subrequest_id"),
        {
          id: "subrequest_id",
          value: reqId,
        },
      ]);
    }
  }, [location.search]);

  // Remove reqId from URL when subrequest_id filter is cleared
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const reqId = searchParams.get("reqId");
    const hasSubrequestFilter = filters.some((f) => f.id === "subrequest_id");

    // If there was a reqId in URL but the filter is now removed, update URL
    if (reqId && !hasSubrequestFilter) {
      searchParams.delete("reqId");
      const newSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true },
      );
    }
  }, [filters, location.pathname, location.search, navigate]);

  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  const handleApprove = async (
    rolesData: Array<any>,
    isAdditional?: boolean,
    comments?: string,
    featureData?: any,
  ) => {
    try {
      setActionLoading(true);
      const result = rolesData?.map((itemData: any) => {
        const featureIds: any[] = Array.from(
          new Set(
            featureData?.rows
              ?.map((data: any) => {
                let all_req_ids: any[] = [];
                let all_add_ids: any[] = [];

                for (const item of data?.subRows ?? []) {
                  if (item.featurename === "Requested") {
                    all_req_ids = item?.subRows
                      ?.filter((d: any) =>
                        itemData?.roleIds?.includes(d?.roleid),
                      )
                      ?.map((d: any) => ({
                        roleId: `${d?.roleid}`,
                        featureId: `${d?.featureid}`,
                        permissionIds: `${d?.permissionid}`,
                        justification: d?.justification ?? "",
                        groupname: d?.groupname ?? "",
                      }));
                  }

                  if (item?.featurename === "Additional") {
                    all_add_ids = item?.subRows
                      ?.filter((d: any) =>
                        itemData?.roleIds?.includes(d?.roleid),
                      )
                      ?.map((d: any) => ({
                        roleId: `${d?.roleid}`,
                        featureId: `${d?.featureid}`,
                        permissionIds: `${d?.permissionid}`,
                        justification: d?.justification ?? "",
                        groupname: d?.groupname ?? "",
                      }));
                  }
                  if (item.featurename === "Existing") {
                    all_req_ids = item?.subRows
                      ?.filter((d: any) =>
                        itemData?.roleIds?.includes(d?.roleid),
                      )
                      ?.map((d: any) => ({
                        roleId: `${d?.roleid}`,
                        featureId: `${d?.featureid}`,
                        permissionIds: `${d?.permissionid}`,
                        justification: d?.justification ?? "",
                        groupname: d?.groupname ?? "",
                      }));
                  }
                }

                return !isAdditional
                  ? [...(all_req_ids || []), ...(all_add_ids || [])]
                  : all_req_ids;
              })
              ?.flat(),
          ),
        );

        return {
          accessId: itemData?.access_id ?? "",
          subrequestId: itemData?.subrequestId ?? "",
          accessRoleList: featureIds,
          approvedRoleIds: itemData?.roleIds ?? [],
          comment: comments,
          isautopopup: !!isAdditional,
        };
      });

      if (result?.[0]?.accessRoleList?.length === 0) {
        triggerToast(`access role list is empty, try again`, "error");
        return;
      }

      const response = await featureRestrictionApproval(result);

      if (response?.status?.toLowerCase() === "success") {
        triggerToast(response?.message, "success");
        setSelectedRows([]);
        handleModalCancle();
        setrefresh((prev) => prev + 1);
      } else {
        triggerToast(response?.message, "error");
      }
    } catch (err) {
      triggerToast("Something went wrong!", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async (
    rolesData: Array<any>,
    comments?: string,
    action?: string,
  ) => {
    const payload = {
      action:
        action === "revoke" || action === "rowselectRevoke"
          ? "revoked"
          : "denied",
    };

    const result = rolesData?.map((d) => {
      return {
        accessRoleId: d?.access_id,
        subrequestId: d?.subrequestId ?? "",
        approvedRoleIds: [],
        deniedRoleIds: d?.roleIds ?? [],
        featureIds: [],
        comment: comments,
      };
    });

    Object?.assign(payload, { accessRoleList: result });

    const response = await adminApprovaDeny(payload);
    if (response?.status?.toLowerCase() === "success") {
      triggerToast(response?.message, "success");
      setSelectedRows([]);
      setrefresh((prev) => prev + 1);
    } else {
      triggerToast(
        response?.message || "Something went wrong! Please try after sometime.",
        "error",
      );
    }
    handleModalCancle();
  };

  const handleRevoke = async (data: any) => {
    const revokeData = data?.row?.original;
    const syncSourceState =
      revokeData?.sync_source === "user_change_process" ? true : false;
    setModalState({
      headLabel: "Revoke Request",
      activateOn: "revoke",
      isOpen: true,
      isSyncSourceUserChangeProcess: syncSourceState,
      payload: [
        {
          access_id: revokeData?.access_id,
          subrequestId: revokeData?.subrequest_id,
          roleIds: [],
        },
      ],
    });
  };

  const handleSelection = (_event: any) => {
    const updatedRows = gridData?.rows?.map((item: any) => {
      if (
        item?.access_id === _event?.row?.access_id &&
        item?.subrequest_id === _event?.row?.subrequest_id
      ) {
        tableDataRef.current[_event?.row?.subrequest_id] = _event?.data;
        return {
          ...item,
          eligible_roles: {
            ...item?.eligible_roles,
            selected: _event?.data ?? [],
          },
          action_id: item?.action_id?.map((action: any) => {
            if (action?.value === "Approve") {
              return { ...action, disabled: _event?.data?.length === 0 };
            }
            return action;
          }),
        };
      }
      return item;
    });
    setGridData({ ...gridData, rows: updatedRows });
  };

  const handleModalCancle = () => {
    setModalState(defaultModalState);
  };

  const getBulkPayload = (rows: Array<any>) => {
    return rows?.map((item: any) => ({
      access_id: item?.access_id,
      subrequestId: item?.subrequest_id,
      roleIds:
        item?.eligible_roles?.selected?.map((role: any) => role?.id) ?? [],
    }));
  };

  const handleBulkDeny = (rows?: Array<any>) => {
    const bulkPayload = getBulkPayload(rows?.length ? rows : selectedData);
    if (bulkPayload?.length > 0) {
      setModalState({
        headLabel: "Deny Request",
        activateOn: "deny",
        isOpen: true,
        payload: bulkPayload,
      });
    }
  };

  const handleBulkApprove = () => {
    if (
      selectedData?.length > 0 &&
      selectedData?.every(
        (item: any) =>
          Array?.isArray(item?.eligible_roles?.selected) &&
          item?.eligible_roles?.selected?.length > 0,
      )
    ) {
      const bulk_payload = selectedData
        ?.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.access_id === item.access_id),
        )
        ?.map((i: any) => {
          return {
            access_id: i?.access_id,
            subrequestId: i?.subrequest_id,
            roleIds:
              i?.eligible_roles?.selected?.map((item: any) => item?.id) ?? [],
          };
        });

      setModalState({
        headLabel: "Approve Request",
        activateOn: "approve",
        isOpen: true,
        payload: bulk_payload,
      });
    } else {
      triggerToast("Please select Roles for All Selected Request.", "error");
    }
  };

  const bulkActionButtonSx = {
    color: "#fff",
    borderColor: "#fff",
    fontSize: "14px !important",
    textTransform: "capitalize",
    "& .MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit": {
      fontSize: "16px !important",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#fff",
    },
  };

  return (
    <>
      <KpiCard
        kpiCards={kpicardsData}
        isHoldView={false}
        handleCardClick={handleKPICardClick}
      />
      <GridTableCommon
        gridData={gridData ?? {}}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        setSelectedData={setSelectedData}
        selectedData={selectedData}
        isRowValid={(row) =>
          row?.new_status === "Awaiting Approval" &&
          Array.isArray(row?.action_id) &&
          row?.action_id?.length > 0
        }
        keyId="subrequest_id"
        title="Access Status"
        pinColumns={["eligible_roles", "action_id"]}
        style={{ height: "calc(100vh - 30.5rem)" }}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        pagination={pagination}
        setPagination={setPagination}
        actions={[
          {
            event: "save",
            component: (
              <IconButton
                onClick={handleDownloadRequests}
                id="AdminApprovalRequestsDownload"
                title="Download"
              >
                <MdOutlineFileDownload
                  style={{ fontSize: "20px" }}
                  id="admin-approval-icon-download"
                />
              </IconButton>
            ),
          },
        ]}
        rowActions={[
          {
            event: "Deny",
            component: (
              <Button
                key="selectbar-action-deny"
                variant="outlined"
                endIcon={<BlockIcon fontSize="inherit" />}
                sx={bulkActionButtonSx}
                onClick={() => handleBulkDeny()}
              >
                Deny
              </Button>
            ),
            callback: (_event: any, d: any) => {
              handleBulkDeny(d);
            },
          },
          {
            event: "Approve",
            component: (
              <Button
                key="selectbar-action-approve"
                variant="outlined"
                endIcon={<CheckIcon fontSize="inherit" />}
                sx={bulkActionButtonSx}
                onClick={handleBulkApprove}
              >
                Approve
              </Button>
            ),
            callback: (_event: any) => {
              handleBulkApprove();
            },
          },
        ]}
        cellActions={[
          {
            event: "Approve",
            callback: (_event: any, data: any) => {
              const approveData = data?.row?.original;
              const syncSourceState =
                approveData?.sync_source === "user_change_process"
                  ? true
                  : false;
              setModalState({
                headLabel: "Approve Request",
                activateOn: "approve",
                isOpen: true,
                payload: [
                  {
                    access_id: approveData?.access_id,
                    subrequestId: approveData?.subrequest_id,
                    roleIds:
                      approveData?.eligible_roles?.selected?.map(
                        (item: any) => item?.id,
                      ) ?? [],
                  },
                ],
                isSyncSourceUserChangeProcess: syncSourceState,
              });
            },
            icon: false,
          },
          {
            event: "MULTI_SELECT",
            callback: (_event: any, data: any) => {
              handleSelection(_event);
            },
            icon: false,
          },
          {
            event: "Deny",
            callback: (_event: any, data: any) => {
              const denyData = data?.row?.original;
              const syncSourceState =
                denyData?.sync_source === "user_change_process" ? true : false;
              setModalState({
                headLabel: "Deny Request",
                activateOn: "deny",
                isOpen: true,
                payload: [
                  {
                    access_id: denyData?.access_id,
                    subrequestId: denyData?.subrequest_id,
                    roleIds:
                      denyData?.eligible_roles?.selected?.map(
                        (item: any) => item?.id,
                      ) ?? [],
                  },
                ],
                isSyncSourceUserChangeProcess: syncSourceState,
              });
            },
            icon: false,
          },
          {
            event: "Revoke",
            callback: (_event: any, data: any) => {
              handleRevoke(data);
            },
            icon: false,
          },
        ]}
        onRowClick={(_event: any, row: any) => {
          const dataRow = row?.original;
          const syncSourceState =
            dataRow?.sync_source === "user_change_process" ? true : false;
          const actions: Array<string> =
            dataRow?.action_id && dataRow?.action_id?.length > 0
              ? dataRow?.action_id?.map((d: any) => d?.value?.toLowerCase())
              : [];

          setModalState({
            headLabel: "User Request",
            activateOn: actions?.includes("approve")
              ? "rowselectApprove"
              : actions?.includes("revoke")
                ? "rowselectRevoke"
                : "noAction",
            isOpen: true,
            payload: [
              {
                access_id: dataRow?.access_id,
                subrequestId: dataRow?.subrequest_id,
                roleIds: dataRow?.eligible_roles?.selected
                  ? dataRow?.eligible_roles?.selected?.map(
                      (item: any) => item?.id,
                    )
                  : [],
              },
            ],
            isSyncSourceUserChangeProcess: syncSourceState,
          });
        }}
        loading={isLoading}
      />

      {modalState?.isOpen && (
        <FeatureRestrictionModal
          modalState={modalState}
          handleClose={handleModalCancle}
          handleApprove={handleApprove}
          handleDeny={handleDeny}
          actionLoading={actionLoading}
        />
      )}
    </>
  );
};

export default AdminApproval;
